import React from 'react';
import { alpha, Theme } from '@mui/material';
import {
  CreateClasses,
  EditClasses,
  ListClasses,
  RadioButtonGroupInputClasses,
  ShowClasses,
} from 'react-admin';
import NoResults from '../components/layout/NoResults';

const components = {
  RaEdit: {
    styleOverrides: {
      root: {
        [`& .${EditClasses.main} > *:first-of-type`]: {
          minWidth: 0,
        },
        [`& .${EditClasses.card}`]: {
          backgroundColor: 'transparent',
          overflow: 'visible',
          maxWidth: '100%',
          boxShadow: 'none',
        },
      },
    },
  },
  RaShow: {
    styleOverrides: {
      root: {
        [`& .${ShowClasses.main} > *:first-of-type`]: {
          minWidth: 0,
        },
      },
    },
  },
  RaCreate: {
    styleOverrides: {
      root: {
        [`& .${CreateClasses.card}`]: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },
  },
  RaList: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        [`& .${ListClasses.main}`]: {
          maxWidth: '100%',
        },
        [`& .${ListClasses.actions}`]: {
          marginBottom: theme.spacing(3),
        },
      }),
    },
  },
  RaLabeled: {
    styleOverrides: {
      root: {
        marginBottom: 0,
        '& .RaLabeled-label': {
          fontSize: 'inherit',
          marginBottom: 0,
        },
      },
    },
  },
  RaSelectInput: {
    styleOverrides: {
      root: {
        minWidth: 200,
        margin: 0,
      },
    },
  },
  RaDateInput: {
    styleOverrides: {
      root: {
        minWidth: 200,
      },
    },
  },
  RaDateTimeInput: {
    styleOverrides: {
      root: {
        minWidth: 200,
      },
    },
  },
  RaFormInput: {
    styleOverrides: {
      root: {
        width: 'auto',
      },
    },
  },
  RaRadioButtonGroupInput: {
    styleOverrides: {
      root: {
        margin: 0,
        [`& .${RadioButtonGroupInputClasses.label}`]: {
          transform: 'none',
        },
      },
    },
  },
  RaBooleanInput: {
    styleOverrides: {
      root: {
        '& .MuiFormControlLabel-root': {
          marginRight: 0,
        },
      },
    },
  },
  RaDatagrid: {
    defaultProps: {
      bulkActionButtons: false,
      rowClick: false,
      empty: <NoResults variant="h6" />,
    },
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        '& .RaDatagrid-rowOdd': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
        '& .RaDatagrid-expandedPanel': {
          backgroundColor: theme.palette.background.expand,
          '& > .MuiTableCell-root': {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
          },
        },
      }),
      bulkActionsDisplayed: {
        root: { marginTop: 100 },
      },
    },
  },
  RaDataTable: {
    defaultProps: {
      bulkActionButtons: false,
      rowClick: false,
      empty: <NoResults variant="h6" />,
    },
    styleOverrides: {
      root: {
        '& .RaDataTable-rowOdd': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      },
      bulkActionsDisplayed: {
        root: { marginTop: 100 },
      },
    },
  },
  RaListToolbar: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        [theme.breakpoints.down('sm')]: {
          backgroundColor: 'transparent',
        },
      }),
    },
  },
  RaTopToolbar: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        [theme.breakpoints.down('sm')]: {
          backgroundColor: 'transparent',
        },
      }),
    },
  },
  RaToolbar: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        marginBottom: '0 !important',
        marginLeft: '0 !important',
        marginRight: '0 !important',
        minHeight: '0 !important',
        marginTop: theme.spacing(4),

        [theme.breakpoints.up('sm')]: {
          backgroundColor: 'transparent',
          paddingRight: theme.spacing(0),
          paddingLeft: theme.spacing(0),
        },
      }),
    },
  },
  RaBulkActionsToolbar: {
    styleOverrides: {
      root: {
        '.RaBulkActionsToolbar-toolbar:not(.RaBulkActionsToolbar-collapsed)': {
          minHeight: 40,
          height: 40,
          transform: 'translateY(-40px)',
        },
      },
    },
  },
  RaTabbedShowLayout: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        '& .RaTabbedShowLayout-content': {
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(4),
        },
      }),
    },
  },
  MuiGrid: {
    styleOverrides: {
      container: {
        width: '100%',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'filled',
      size: 'small',
    },
  },
  MuiTabs: {
    defaultProps: {
      variant: 'scrollable',
      scrollButtons: 'auto',
    },
  },
  MuiFormControl: {
    defaultProps: {
      variant: 'filled',
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
      color: 'secondary',
    },
  },
  MuiButton: {
    defaultProps: {
      color: 'secondary',
    },
    styleOverrides: {
      root: ({ ownerState, theme }: { ownerState: any; theme: Theme }) => ({
        ...(ownerState.variant === 'outlined' && {
          color: theme.palette.emphasisText.main,
          borderColor: alpha(theme.palette.emphasisText.main, 0.5),
          '&:hover': {
            backgroundColor: alpha(theme.palette.emphasisText.main, 0.08),
          },
        }),
      }),
    },
  },
  RaAutocompleteInput: {
    styleOverrides: {
      root: {
        '& .RaAutocompleteInput-textField': {
          minWidth: 200,
        },
      },
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: 'filled',
    },
    styleOverrides: {
      root: {
        minWidth: 200,
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'always',
    },
    styleOverrides: {
      root: ({ ownerState }: any) => ({
        ...(ownerState.underline === 'always' && {
          textDecoration: 'underline',
        }),
        '&:hover': {
          textDecoration: 'underline',
        },
      }),
    },
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: {
        variant: 'h6',
      },
    },
    styleOverrides: {
      action: {
        marginTop: '-4px',
        marginRight: '-4px',
      },
      root: {
        color: '#0B2920',
        backgroundColor: '#12413210',
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      day: {
        fontWeight: '300',
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      root: {
        height: '64px',
      },
    },
  },
  MuiPickersCalendar: {
    styleOverrides: {
      transitionContainer: {
        marginTop: '6px',
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      iconButton: {
        backgroundColor: 'transparent',
        '& > *': {
          backgroundColor: 'transparent',
        },
      },
      switchHeader: {
        marginTop: '2px',
        marginBottom: '4px',
      },
    },
  },
  MuiPickersClock: {
    styleOverrides: {
      container: {
        margin: '32px 0 4px',
      },
    },
  },
  MuiPickersClockNumber: {
    styleOverrides: {
      clockNumber: {
        left: 'calc(50% - 16px)',
        width: '32px',
        height: '32px',
      },
    },
  },
  MuiPickerDTHeader: {
    styleOverrides: {
      dateHeader: {
        '& h4': {
          fontSize: '2.125rem',
          fontWeight: 400,
        },
      },
      timeHeader: {
        '& h3': {
          fontSize: '3rem',
          fontWeight: 400,
        },
      },
    },
  },
  MuiPickersTimePicker: {
    styleOverrides: {
      hourMinuteLabel: {
        '& h2': {
          fontSize: '3.75rem',
          fontWeight: 300,
        },
      },
    },
  },
  MuiPickersToolbar: {
    styleOverrides: {
      toolbar: {
        '& h4': {
          fontSize: '2.125rem',
          fontWeight: 400,
        },
      },
    },
  },
  MuiChip: {
    defaultProps: {
      color: 'neutral',
    },
  },
  RaSaveButton: {
    defaultProps: {
      color: 'secondary',
    },
  },
} as const;

export default components;

declare module '@mui/material/Select' {
  interface SelectClasses {
    root: string;
  }
}
